import type {
  AlertSearch,
  AlertSubscribe,
  AlertView,
  AnswerResult,
  Application,
  AppView,
  Billing,
  CareerApplication,
  CareerForm,
  Decision,
  DecisionResult,
  EmailPrefs,
  EngineEvent,
  Fit,
  Funnel,
  Job,
  JobDetail,
  Onboarding,
  Outcome,
  Plan,
  Profile,
  ProfileView,
  PublicJob,
  Question,
  SearchParams,
  Stats,
  UnsubscribeInfo,
} from "../../shared/types.ts";

export class ApiError extends Error {
  constructor(
    readonly error: string,
    readonly status: number,
    /** The form field a `bad_application` names. */
    readonly field?: string,
  ) {
    super(error);
  }
}

type TokenSource = () => Promise<string | null>;

let token: TokenSource = async () => null;
let signedOut = () => {};

/** Where the bearer token comes from, and what to do when the API answers 401. App wires both to Supabase. */
export function configureApi(opts: { token?: TokenSource; onSignedOut?: () => void }): void {
  if (opts.token) token = opts.token;
  if (opts.onSignedOut) signedOut = opts.onSignedOut;
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (json !== undefined) headers.set("content-type", "application/json");
  const t = await token();
  if (t) headers.set("authorization", `Bearer ${t}`);
  const res = await fetch(path, { ...rest, headers, body: json !== undefined ? JSON.stringify(json) : rest.body });
  const data = await res.json().catch(() => null);
  if (res.status === 401) signedOut();
  const err = data as { error?: string; field?: unknown } | null;
  if (!res.ok) throw new ApiError(err?.error ?? (res.statusText || "request_failed"), res.status, typeof err?.field === "string" ? err.field : undefined);
  return data as T;
}

/** What each worker error code means to the person using the app. */
export const ERROR_TEXT: Record<string, string> = {
  not_found: "That job isn't listed anymore.",
  job_closed: "This posting just closed.",
  not_retryable: "That one isn't waiting on a retry.",
  already_sent: "That one may have gone through already.",
  bad_answers: "One of those answers is too long.",
  answers_missing: "Answer every question to send it.",
  unknown_question: "Those questions changed. Reload and try again.",
  too_many_answers: "Your profile holds too many saved answers.",
  bad_cursor: "Search lost its place. Try again.",
  bad_path: "Your resume didn't attach. Upload it again.",
  daily_cap: "You've hit today's limit. Send more tomorrow.",
  plan_limit: "You've used this month's applications. Upgrade to send more.",
  already_on_plan: "You're already on that plan.",
  billing_unavailable: "Billing isn't available right now. Try again soon.",
  no_customer: "There's no billing to manage yet.",
  signed_out: "You're signed out. Log in again.",
  no_model: "Fit reads aren't available right now.",
  model_failed: "The fit read didn't load. Try again.",
  rate_limited: "Too many fit reads at once. Try again in a minute.",
  bad_id: "That job isn't listed anymore.",
  api_unreachable: "Can't reach headcount. Check your connection.",
  cancel_failed: "Your plan couldn't be canceled, so nothing was deleted. Try again soon.",
  account_unavailable: "Deleting accounts isn't available right now. Try again soon.",
  unknown_role: "That role isn't listed anymore.",
  role_closed: "This role isn't taking applications anymore.",
  already_applied: "You've already applied to this role.",
  not_editable: "Your application is in review, so it can't change now.",
  not_withdrawable: "This application is already closed.",
  bad_application: "Check the highlighted fields.",
  resume_missing: "Your resume didn't upload. Upload it again.",
  resume_not_pdf: "Upload your resume as a PDF.",
  careers_unavailable: "Applications aren't available right now. Try again soon.",
};

/** A plain sentence for a failed call: the worker's code when it has one, otherwise `fallback`. */
export function errorText(e: unknown, fallback = "That didn't save. Try again."): string {
  if (e instanceof ApiError) return ERROR_TEXT[e.error] ?? fallback;
  if (e instanceof TypeError) return ERROR_TEXT.api_unreachable!;
  return fallback;
}

const id = (s: string) => encodeURIComponent(s);

/** The `GET /api/jobs` query string. Arrays join with commas, booleans become 1 or 0, empty values drop. */
export function searchQuery(p: SearchParams): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      if (v.length) q.set(k, v.join(","));
    } else if (typeof v === "boolean") q.set(k, v ? "1" : "0");
    else q.set(k, String(v).trim());
  }
  return q.toString();
}

export const isJob = (j: Application["job"]): j is Job => "title" in j;

export type Me = { user: { id: string; email: string }; onboarding: Onboarding };
export type Page = { items: Job[]; nextCursor: string | null };
export type ProfilePatch = Partial<Omit<Profile, "prefs" | "workAuth" | "relocation" | "links">> & {
  prefs?: Partial<Omit<Profile["prefs"], "minSalary">> & { minSalary?: number | null };
  workAuth?: Partial<Profile["workAuth"]>;
  relocation?: Partial<Profile["relocation"]>;
  links?: Partial<Profile["links"]>;
};

export const getMe = () => api<Me>("/api/me");
export const sendRef = (ref: string) => api<{ ok: true }>("/api/me/ref", { method: "POST", json: { ref } });
export const getStats = () => api<Stats>(`/api/me/stats?tz=${new Date().getTimezoneOffset()}`);
export const getProfile = () => api<ProfileView>("/api/profile");
export const saveProfile = (patch: ProfilePatch) => api<ProfileView>("/api/profile", { method: "PATCH", json: patch });
export const setResume = (path: string) => api<ProfileView>("/api/profile/resume", { method: "POST", json: { path } });

export function getFeed(limit: number, excludeIds: string[] = []): Promise<{ items: Job[] }> {
  const q = new URLSearchParams({ limit: String(limit) });
  if (excludeIds.length) q.set("excludeIds", excludeIds.join(","));
  return api(`/api/feed?${q}`);
}

export const searchJobs = (p: SearchParams, init?: RequestInit) => api<Page>(`/api/jobs?${searchQuery(p)}`, init);
export const getJob = (jobId: string, init?: RequestInit) => api<JobDetail>(`/api/jobs/${id(jobId)}`, init);
export const getFit = (jobId: string) => api<Fit>(`/api/jobs/${id(jobId)}/fit`);

/** A scraped link the browser may open: http(s) only, never `javascript:` or `data:`. */
export const webUrl = (u: string | null | undefined): string | null => (u && /^https?:\/\//i.test(u) ? u : null);

/** Records a decision. A job the engine can't fill opens on the company site from the click, so the popup isn't blocked. */
export function decide(job: Pick<Job, "id" | "canAutoApply" | "applyUrl" | "jobUrl">, decision: Decision): Promise<DecisionResult> {
  const site = webUrl(job.applyUrl) ?? webUrl(job.jobUrl);
  if (decision === "apply" && !job.canAutoApply && site && typeof window !== "undefined") window.open(site, "_blank", "noopener,noreferrer");
  return api<DecisionResult>(`/api/jobs/${id(job.id)}/decision`, { method: "POST", json: { decision } });
}

export const undecide = (jobId: string) => api<{ removed: boolean; cancelled: boolean }>(`/api/jobs/${id(jobId)}/decision`, { method: "DELETE" });
export const getSaved = () => api<(Job & { savedAt: string })[]>("/api/saved");
export const getApplications = (status?: string) => api<Application[]>(`/api/applications${status ? `?status=${id(status)}` : ""}`);
export const retry = (jobId: string) => api<{ ok: boolean; application: Application }>(`/api/applications/${id(jobId)}/retry`, { method: "POST" });
export const answer = (jobId: string, answers: Question[]) =>
  api<AnswerResult>(`/api/applications/${id(jobId)}/answers`, { method: "POST", json: { answers } });
export const setOutcome = (jobId: string, outcome: Outcome | null) =>
  api<AppView>(`/api/applications/${id(jobId)}/outcome`, { method: "PUT", json: { outcome } });
export const getFunnel = () => api<Funnel>("/api/funnel");
export const getEvents = (since?: string | null) =>
  api<{ events: EngineEvent[]; now: string }>(`/api/events${since ? `?since=${id(since)}` : ""}`);
export const getBilling = () => api<Billing>("/api/billing");
export const startCheckout = (plan: Exclude<Plan, "free">) => api<{ url: string }>("/api/billing/checkout", { method: "POST", json: { plan } });
export const openPortal = () => api<{ url: string }>("/api/billing/portal", { method: "POST" });
export const deleteAccount = () => api<{ ok: true }>("/api/account", { method: "DELETE" });
export const getCareerApplications = () => api<CareerApplication[]>("/api/careers/applications");
export const applyToRole = (role: string, form: CareerForm) =>
  api<CareerApplication>(`/api/careers/applications/${id(role)}`, { method: "POST", json: form });
export const editApplication = (role: string, form: CareerForm) =>
  api<CareerApplication>(`/api/careers/applications/${id(role)}`, { method: "PUT", json: form });
/** A signed-out application: the form, the email and the resume in one multipart request. The browser never touches Storage. */
export function applyAsGuest(role: string, form: CareerForm, email: string, resume: File): Promise<{ ok: true }> {
  const body = new FormData();
  for (const [k, v] of Object.entries(form)) body.set(k, String(v));
  body.set("email", email);
  body.set("resume", resume);
  return api<{ ok: true }>(`/api/careers/guest/${id(role)}`, { method: "POST", body });
}
export const withdrawApplication = (role: string) => api<CareerApplication>(`/api/careers/applications/${id(role)}/withdraw`, { method: "POST" });
export const getEmailPrefs = () => api<EmailPrefs>("/api/email-prefs");
export const saveEmailPrefs = (patch: Partial<EmailPrefs>) => api<EmailPrefs>("/api/email-prefs", { method: "PUT", json: patch });
/** What an email's unsubscribe link turns off. Signed out works; the token is the whole credential. */
export const getUnsubscribe = (t: string) => api<UnsubscribeInfo>(`/api/unsubscribe?t=${id(t)}`);
/** Turns that email off. Only from the button, never on page load, since mail scanners open links. */
export const unsubscribe = (t: string) => api<UnsubscribeInfo>(`/api/unsubscribe?t=${id(t)}`, { method: "POST" });
/** The public jobs page's search, signed in or out. Empty values drop. */
export function publicJobs(s: { q: string; location: string; cursor?: string | null }, init?: RequestInit) {
  const p = new URLSearchParams();
  if (s.q) p.set("q", s.q);
  if (s.location) p.set("location", s.location);
  if (s.cursor) p.set("cursor", s.cursor);
  return api<{ items: PublicJob[]; nextCursor: string | null }>(`/api/public/jobs?${p}`, init);
}
/** Starts a job alert for a search; the first jobs go out to `email` right away. */
export const subscribeAlert = (body: AlertSubscribe) => api<{ ok: true }>("/api/alerts", { method: "POST", json: body });
/** The alert an email's edit link opens. Signed out works; the token is the whole credential. */
export const getAlert = (t: string) => api<AlertView>(`/api/alerts/edit?t=${id(t)}`);
export const saveAlert = (t: string, search: AlertSearch) => api<AlertView>(`/api/alerts/edit?t=${id(t)}`, { method: "PUT", json: search });
